import type {
  WorkflowDefinitionDocument,
  WorkflowStepDeclaration,
} from './platform-workflow-definition-lifecycle.models';
import type { WorkflowRequirementProvider } from '@app/shared/workflow-commands/workflow-completion-requirements';
import {
  domainBlockCodes,
  noticeBlockCodes,
  noticeBlockManifests,
  noticeCommandCodes,
} from '@app/phms/workflow-blocks/domain-block-manifests';
import {
  NOTICE_REQUIREMENT,
  noticeRequirementIssue,
} from '@app/phms/workflow-blocks/notice-policy';
import {
  addWorkflowCapability,
  installedReference,
  sameCapabilityReference,
  WorkflowCapabilityCatalogEntry,
} from './workflow-capability-authoring';
import { replacePresentation } from './workflow-presentation-authoring';
import { resolveNoticeSource } from './workflow-notice-source';
import type { DomainSource } from './workflow-domain-source.models';
export type { DomainSource } from './workflow-domain-source.models';
/** One group survives removing its completion binding; publication pins its exact read/command closure. */
export function ensureNoticeGroup(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  settings: Record<string, unknown>,
  capabilities: readonly WorkflowCapabilityCatalogEntry[],
  selectedSource?: DomainSource,
): WorkflowDefinitionDocument {
  const issue = noticeRequirementIssue(settings, false);
  if (issue) throw new Error(issue);
  const node = document.nodes.find((n) => n.id === nodeId),
    declaration = node?.declaration;
  if (!declaration)
    throw new Error('Initialize one work step before adding notice work.');
  const domains = declaration.detail.blocks.filter((b) =>
    domainBlockCodes.has(b.code),
  );
  const resolution = resolveNoticeSource(
    document,
    nodeId,
    selectedSource?.bindingMode === 'process-reference'
      ? selectedSource.referenceField
      : undefined,
  );
  if (resolution.kind !== 'resolved')
    throw new Error(
      resolution.kind === 'blocked'
        ? resolution.message
        : resolution.kind === 'choice'
          ? 'Choose a Hearing reference for this step.'
          : 'Add a Hearing reference for this workflow before adding notice work.',
    );
  const source = resolution.source;
  const notices = domains.filter((b) => noticeBlockCodes.has(b.code));
  if (
    notices.some(
      (b) => b.settings['noticeGroupId'] !== settings['noticeGroupId'],
    )
  )
    throw new Error(
      'This step already has another notice group. Reuse its stable group identity.',
    );
  const manifest = noticeBlockManifests.find(
    (m) => m.code === 'notices.delivery',
  )!;
  let next = document;
  for (const read of manifest.reads) {
    const entries = capabilities.filter(
      (c) =>
        c.key.provider === 'notices' &&
        c.key.kind === 'view' &&
        c.key.code === read.code &&
        c.key.version === read.version,
    );
    if (entries.length !== 1)
      throw new Error('The exact notice reader is unavailable.');
    next = addWorkflowCapability(next, entries[0], capabilities);
  }
  const commands = [...declaration.behavior.commands];
  for (const code of noticeCommandCodes) {
    const entries = capabilities.filter(
      (c) =>
        c.key.provider === 'notices' &&
        c.key.kind === 'command' &&
        c.key.code === code &&
        c.key.version === 1,
    );
    if (entries.length !== 1)
      throw new Error('The exact notice command dependencies are unavailable.');
    next = addWorkflowCapability(next, entries[0], capabilities);
    const capability = installedReference(entries[0]);
    const existing = commands.filter(
      (c) =>
        c.capability.key.provider === 'notices' &&
        c.capability.key.code === code,
    );
    if (
      existing.length > 1 ||
      (existing.length === 1 &&
        !sameCapabilityReference(existing[0].capability, capability))
    )
      throw new Error(
        'A saved notice command has another contract. Inspect it before replacing it.',
      );
    if (!existing.length)
      commands.push({ capability, requirements: [], outcomes: [] });
  }
  // A notice-only step still needs an explicit way to finish. Preserve steps whose
  // other task commands already own their completion behavior.
  if (
    declaration.behavior.commands.every(
      (c) => c.capability.key.provider === 'notices',
    )
  ) {
    const complete = capabilities.filter(
      (c) =>
        c.key.provider === 'workflow' &&
        c.key.kind === 'command' &&
        c.key.code === 'workflow.step.complete' &&
        c.key.version === 1,
    );
    if (complete.length !== 1)
      throw new Error(
        'The installed Complete step action is unavailable. Reload the workflow editor.',
      );
    next = addWorkflowCapability(next, complete[0], capabilities);
    commands.push({
      capability: installedReference(complete[0]),
      requirements: [],
      outcomes: [],
    });
  }
  const blockSettings = { ...source, ...settings };
  const blocks = declaration.detail.blocks.map((b) =>
    noticeBlockCodes.has(b.code)
      ? { ...b, settings: { ...b.settings, ...blockSettings } }
      : b,
  );
  if (!blocks.some((b) => b.code === manifest.code))
    blocks.push({
      instanceId: `notices.delivery-${crypto.randomUUID()}`,
      provider: 'notices',
      code: manifest.code,
      version: 1,
      settingsVersion: 1,
      settings: blockSettings,
      region: 'main',
      width: 'full',
    });
  return replacePresentation(next, nodeId, {
    ...declaration,
    detail: { ...declaration.detail, blocks },
    behavior: { ...declaration.behavior, commands },
  });
}
export function changeNoticePolicy(
  declaration: WorkflowStepDeclaration,
  policyJson: string,
): WorkflowStepDeclaration {
  return {
    ...declaration,
    detail: {
      ...declaration.detail,
      blocks: declaration.detail.blocks.map((b) =>
        noticeBlockCodes.has(b.code)
          ? { ...b, settings: { ...b.settings, policyJson } }
          : b,
      ),
    },
    behavior: {
      ...declaration.behavior,
      completionRequirements: declaration.behavior.completionRequirements?.map(
        (r) =>
          r.provider === NOTICE_REQUIREMENT
            ? { ...r, settings: { ...r.settings, policyJson } }
            : r,
      ),
    },
  };
}
export function compatibleHearingFields(
  document: WorkflowDefinitionDocument,
  sourceVersion = 1,
) {
  return (document.processData?.fields ?? []).filter(
    (field) =>
      field.type === 'record-reference' &&
      (field.recordTypeCode === 'scheduled-proceeding' ||
        (sourceVersion === 2 &&
          field.recordTypeCode === 'incarcerated-person')),
  );
}
export function sourceSettings(
  settings: Record<string, unknown>,
): DomainSource {
  return settings['bindingMode'] === 'process-reference'
    ? {
        bindingMode: 'process-reference',
        ...(typeof settings['referenceField'] === 'string'
          ? { referenceField: settings['referenceField'] }
          : {}),
      }
    : { bindingMode: 'linked-subject' };
}
/** All six blocks and their SDMF gate use one visit source; callers emit this declaration once. */
export function changeDomainSource(
  declaration: WorkflowStepDeclaration,
  source: DomainSource,
): WorkflowStepDeclaration {
  const settings = {
    bindingMode: source.bindingMode,
    ...(source.bindingMode === 'process-reference' && source.referenceField
      ? { referenceField: source.referenceField }
      : {}),
  };
  return {
    ...declaration,
    detail: {
      ...declaration.detail,
      blocks: declaration.detail.blocks.map((block) => {
        if (!domainBlockCodes.has(block.code)) return block;
        const {
          bindingMode: _bindingMode,
          referenceField: _referenceField,
          ...other
        } = block.settings;
        return { ...block, settings: { ...other, ...settings } };
      }),
    },
    behavior: {
      ...declaration.behavior,
      completionRequirements: declaration.behavior.completionRequirements?.map(
        (requirement) =>
          requirement.provider === 'sdmf.completed'
            ? {
                ...requirement,
                settings: {
                  ...settings,
                  completionPolicy: 'sdmf.completion.v1',
                },
              }
            : requirement,
      ),
    },
  };
}
export function requireSdmf(
  declaration: WorkflowStepDeclaration,
  required: boolean,
  provider?: WorkflowRequirementProvider,
): WorkflowStepDeclaration {
  const blocks = declaration.detail.blocks.filter(
    (block) => block.code === 'sdmf.analysis' && block.provider === 'sdmf',
  );
  if (blocks.length !== 1)
    throw new Error(
      'Add one SDMF analysis block before configuring its requirement.',
    );
  const existing = (declaration.behavior.completionRequirements ?? []).filter(
    (requirement) => requirement.provider !== 'sdmf.completed',
  );
  if (required) {
    if (
      !provider ||
      provider.version !== blocks[0].version ||
      provider.settingsVersion !== blocks[0].version ||
      !provider.settingsHash
    )
      throw new Error('The installed SDMF completion policy is unavailable.');
    existing.push({
      id: 'sdmf-completed',
      provider: provider.provider,
      version: provider.version,
      settingsVersion: provider.settingsVersion,
      settingsHash: provider.settingsHash,
      settings: {
        ...sourceSettings(blocks[0].settings),
        completionPolicy: 'sdmf.completion.v1',
      },
    });
  }
  return {
    ...declaration,
    behavior: { ...declaration.behavior, completionRequirements: existing },
  };
}
